import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Lock, Mail, ArrowUpRight } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

function formatErr(detail) {
  if (!detail) return "Greška. Pokušajte ponovo.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function AdminLogin() {
  const { login, isAuthed } = useAuth();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  if (isAuthed) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(form.email, form.password);
      toast.success("Dobrodošli");
      navigate("/admin");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Pogrešni podaci");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#070707] text-white flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <div className="text-[10px] tracking-[0.42em] text-[#c5a572] uppercase mb-3">
            Stanimirović — Admin
          </div>
          <h1 className="font-serif text-4xl tracking-tight">Prijavite se</h1>
          <p className="text-white/55 mt-3 font-light text-sm">
            Pristup porudžbinama i kontakt porukama.
          </p>
        </div>
        <form
          onSubmit={submit}
          data-testid="admin-login-form"
          className="bg-[#0d0d0d] border border-white/10 p-8 space-y-6"
        >
          <Field
            icon={<Mail size={14} />}
            type="email"
            label="Email"
            testid="admin-login-email"
            value={form.email}
            onChange={(v) => setForm({ ...form, email: v })}
            required
          />
          <Field
            icon={<Lock size={14} />}
            type="password"
            label="Lozinka"
            testid="admin-login-password"
            value={form.password}
            onChange={(v) => setForm({ ...form, password: v })}
            required
          />
          <button
            type="submit"
            data-testid="admin-login-submit"
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 bg-[#c5a572] hover:bg-[#d4b685] disabled:opacity-60 text-[#0a0a0a] px-6 py-3.5 rounded-full text-[11px] tracking-[0.28em] uppercase font-medium transition-colors"
          >
            {loading ? "Prijava..." : "Prijava"}
            <ArrowUpRight size={14} />
          </button>
        </form>
        <div className="mt-6 text-center">
          <a href="/" className="text-[11px] tracking-[0.28em] uppercase text-white/40 hover:text-white">
            ← Nazad na sajt
          </a>
        </div>
      </div>
    </div>
  );
}

function Field({ icon, label, type, testid, value, onChange, required }) {
  return (
    <div>
      <label className="block text-[10px] tracking-[0.32em] uppercase text-white/50 mb-2 flex items-center gap-2">
        {icon} {label}
      </label>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        className="w-full bg-transparent border-b border-white/15 focus:border-[#c5a572] outline-none py-3 text-white font-light transition-colors"
      />
    </div>
  );
}
